/* eslint-disable react-hooks/exhaustive-deps */
import { gql, useQuery } from '@apollo/client';
import { Box, Center, Container, Flex, Tab, TabList, TabPanel, TabPanels, Tabs, Text } from '@chakra-ui/react';
import moment from 'moment';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import 'react-super-responsive-table/dist/SuperResponsiveTableStyle.css';
import BreadcrumbComponent from '../../../components/BreadcrumbComponent/BreadcrumbComponent';
import Filter from '../../../components/MarketDevelopment/Miscellaneous/summarizedFilter.js';
import FinalSpreadSheetTable from '../../../components/MarketDevelopment/MonthlySale/Dashboard/final-spread-sheet-table.js';
import MonthlyGrowthSpreadSheetTable from '../../../components/MarketDevelopment/MonthlySale/Dashboard/monthly-growth-sheet-table.js';
import SummarizedSpreadSheetTable from '../../../components/MarketDevelopment/MonthlySale/Dashboard/summarized-sheet-table.js';
import PrintLayout from '../../../components/Print/PrintLayout';
import c from '../../../constant';
import { isValidMonth } from '../../../utils/validation.js';
import { getQueryString } from '../../MarketDevelopment/Utils';
import ExcelSummarizedReportGLM from './Excel/ExcelSummarizedReportGLM.js';
import ExcelSummarizedReportMonthlyComparison from './Excel/ExcelSummarizedReportMonthlyComparison.js';
import ExcelSummarizedReportTotal from './Excel/ExcelSummarizedReportTotal.js';

const tabTitles = ['Total', 'Monthly Comparison', 'General', 'Life', 'Micro', 'GI include MI', 'LI include MI'];

const tabType = {
	2: 'General',
	3: 'Life',
	4: 'Micro',
	5: 'GeneralIncludeMicro',
	6: 'LifeIncludeMicro',
};

const Q_DATASET = gql`
	query get($year: Float!, $monthFrom: Float, $monthTo: Float) {
		getMonthlyGrowthReport(year: $year, monthFrom: $monthFrom, monthTo: $monthTo)
	}
`;

const Q_DATASET_FINAL_REPORT = gql`
	query get($year: Float!, $month: Float!) {
		getMonthlyFinalReport(year: $year, month: $month)
	}
`;

const Q_DATASET_INSURANCE_REPORT = gql`
	query get($type: MDDataSummaryReportEnumType!, $year: Float!, $month: Float!) {
		getMonthlySummaryReport(type: $type, year: $year, month: $month)
	}
`;

function MonthlySummarizedReportDM() {
	const location = useLocation();
	const { replace, push } = useHistory();
	const urlParams = new URLSearchParams(location.search);
	const tab = urlParams.get('tab') ?? 'Total';
	const month = urlParams.get('month') ?? null;
	const year = urlParams.get('year') ?? null;
	const monthFrom = urlParams.get('monthFrom') ?? null;
	const monthTo = urlParams.get('monthTo') ?? null;
	const { t } = useTranslation();

	const selectedYear = year ? parseInt(year) : moment().year();
	const selectedMonth = month ? parseInt(month) : moment().month();

	const tabIndex = useMemo(() => {
		return Math.max(0, tabTitles.indexOf(tab));
	}, [tab]);

	const currentType = tabType[tabIndex] ?? null;

	const onTabChange = (index) => {
		const params = { tab: tabTitles[index], month, year, monthFrom, monthTo };
		const queryString = getQueryString(params);
		const url = '?' + queryString;
		replace(url);
	};

	const { data: monthlyGrowthData } = useQuery(Q_DATASET, {
		variables: {
			year: selectedYear,
			monthFrom: isValidMonth(monthFrom) ? parseInt(monthFrom) : null,
			monthTo: isValidMonth(monthTo) ? parseInt(monthTo) : null,
		},
		fetchPolicy: 'no-cache',
	});

	const { data: insuranceMarketData } = useQuery(Q_DATASET_FINAL_REPORT, {
		variables: { year: selectedYear, month: selectedMonth },
		fetchPolicy: 'no-cache',
	});

	const { data: insuranceData } = useQuery(Q_DATASET_INSURANCE_REPORT, {
		variables: { year: selectedYear, month: selectedMonth, type: currentType },
		fetchPolicy: 'no-cache',
		skip: !currentType,
	});

	return (
		<PrintLayout>
			<>
				<Container maxW="container.xl" mt="16px" mb="16px">
					<BreadcrumbComponent
						list={[
							{
								name: 'Insurance Market Data',
								path: `/insurance-market-data`,
							},
							{
								name: 'Monthly Summarized Report',
								path: '#',
							},
						]}
					/>
					<Center className="show-only-printing">
						<Text textAlign="center" color="#62B3ED" fontFamily="Moul" fontSize={18}>
							{t('Insurance Institute Report')}
						</Text>
					</Center>
					<Center mt="5">
						<Box
							w="100%"
							bg="white"
							borderRadius={c.borderRadius}
							border="1px solid #dbdbdb"
							boxShadow="0 1px 2px rgba(var(--black, #000), 0.1)"
							pb="32px"
						>
							<Flex
								className="hidden-print responsive-header-for-table"
								bg="#FAFAFA"
								p="16px"
								pt="8px"
								pb="8px"
								mb="16px"
								alignItems="center"
								justifyContent="space-between"
								borderBottom="1px solid #dbdbdb"
							>
								<Text fontSize="x-large" fontWeight="bold">
									{t('Monthly Summarized Report')}
								</Text>
								<Flex>
									<Flex mr="2">
										{tabIndex === 0 && (
											<ExcelSummarizedReportTotal
												tab={tab}
												year={selectedYear}
												marketGrowthItem={monthlyGrowthData?.getMonthlyGrowthReport}
											/>
										)}
										{tabIndex === 1 && (
											<ExcelSummarizedReportMonthlyComparison
												tab={tab}
												year={selectedYear}
												month={selectedMonth}
												insuranceMarketItem={insuranceMarketData?.getMonthlyFinalReport}
												generalDataComparisonItem={insuranceMarketData?.getMonthlyFinalReport}
												lifeDataComparisonItem={insuranceMarketData?.getMonthlyFinalReport}
											/>
										)}
										{tabIndex >= 2 && (
											<ExcelSummarizedReportGLM
												tab={tab}
												year={selectedYear}
												month={selectedMonth}
												AsOf={insuranceData?.getMonthlySummaryReport}
												During={insuranceData?.getMonthlySummaryReport}
												By={insuranceData?.getMonthlySummaryReport}
											/>
										)}
									</Flex>
									<Filter
										tab={tab}
										month={month}
										year={year}
										monthFrom={monthFrom}
										monthTo={monthTo}
										onCallback={(url) => push(`/insurance-market-data/industry-monthly-summarized-report${url}`)}
									/>
								</Flex>
							</Flex>
							<Box pl={4} pr={4} pb={4} pt={4} overflow="clip">
								<Tabs
									variant="enclosed"
									width="100%"
									borderRadius={c.borderRadius}
									border="0px solid #dbdbdb"
									index={tabIndex}
									onChange={onTabChange}
								>
									<TabList borderBottomWidth="1px" borderBottomStyle="solid" borderBottomColor="gray.200">
										{tabTitles?.map((title) => (
											<Tab key={title} _focus={{ boxShadow: 'none' }}>
												<Text>{t(title)}</Text>
											</Tab>
										))}
									</TabList>
									<Box pl={0} pr={0} pb={4} pt={4} overflow="clip">
										<TabPanels>
											<TabPanel pl={0} pr={0} pb={0}>
												<MonthlyGrowthSpreadSheetTable year={year} monthFrom={monthFrom} monthTo={monthTo} />
											</TabPanel>
											<TabPanel pl={0} pr={0} pb={0}>
												<FinalSpreadSheetTable month={month} year={year} />
											</TabPanel>
											<TabPanel pl={0} pr={0} pb={0}>
												<SummarizedSpreadSheetTable type={'General'} month={month} year={year} />
											</TabPanel>
											<TabPanel pl={0} pr={0} pb={0}>
												<SummarizedSpreadSheetTable type={'Life'} month={month} year={year} />
											</TabPanel>
											<TabPanel pl={0} pr={0} pb={0}>
												<SummarizedSpreadSheetTable type={'Micro'} month={month} year={year} />
											</TabPanel>
											<TabPanel pl={0} pr={0} pb={0}>
												<SummarizedSpreadSheetTable type={'GeneralIncludeMicro'} month={month} year={year} />
											</TabPanel>
											<TabPanel pl={0} pr={0} pb={0}>
												<SummarizedSpreadSheetTable type={'LifeIncludeMicro'} month={month} year={year} />
											</TabPanel>
										</TabPanels>
									</Box>
								</Tabs>
							</Box>
						</Box>
					</Center>
				</Container>
			</>
		</PrintLayout>
	);
}

export default MonthlySummarizedReportDM;
